import { memo, useEffect, useRef, useState } from "react";

// mui components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import LoadingButton from "@mui/lab/LoadingButton";
import Stack from "@mui/material/Stack";
import { Icon } from "@iconify/react";
import { useMutation } from "@tanstack/react-query";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
// constants, data and custom components
import Map from "./Map";
import { issueImgUpload } from "src/components/ServiceRequest/serviceRequest.hook";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import { AMENITIES } from "../../components/charger/charger.constants";
import { is } from "src/util/is";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

/**
 * Parses Google Maps address components into a unified format.
 * Implements specific fallbacks for India to prioritize district/city names over broad divisions.
 * @param {Array} addressComponents - Raw address components from Google Maps API
 * @param {string} formattedAddress - The human-readable address string
 * @returns {Object|null} Parsed address fields or null
 */
export const parseGoogleAddress = (addressComponents, formattedAddress) => {
  if (!addressComponents) return null;
  
  let country = "";
  let state = "";
  let city = "";
  let zipCode = "";

  // Extract base components
  addressComponents.forEach(comp => {
    if (comp.types.includes("country")) country = comp.short_name;
    if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
    if (comp.types.includes("postal_code")) zipCode = comp.long_name;
  });

  if (country === "IN") {
    /**
     * Optimized Indian City Selection:
     * Google Maps often places "Division" (e.g., Bangalore Division) at administrative_area_level_2.
     * We prefer administrative_area_level_2, but fallback to level_3 or locality if 
     * level_2 is a broad administrative division.
     */
    const level2 = addressComponents.find(c => c.types.includes("administrative_area_level_2"));
    const level3 = addressComponents.find(c => c.types.includes("administrative_area_level_3"));
    const locality = addressComponents.find(c => c.types.includes("locality"));

    if (level2 && !level2.long_name.toLowerCase().includes("division")) {
        city = level2.long_name;
    } else if (level3 && !level3.long_name.toLowerCase().includes("division")) {
        city = level3.long_name;
    } else if (locality) {
        city = locality.long_name;
    } else {
        city = (level2 || level3 || locality)?.long_name || "";
    }
  } else {
    // Standard Global logic for other countries
    const cityObj = addressComponents.find(c => 
        c.types.includes("locality") || 
        c.types.includes("postal_town") || 
        c.types.includes("administrative_area_level_2")
    );
    if (cityObj) city = cityObj.long_name;
  }

  /**
   * Cleans administrative noise from city names to ensure compatibility with OCPI standards.
   * Strips common suffixes like "District", "Division", "Urban", etc.
   */
  const formatCityName = (name) => {
    if (!name) return name;
    return name.replace(/\s+(District|Urban|Rural|Division|Region)$/i, "");
  };

  return {
    country,
    state,
    zipCode,
    city: formatCityName(city),
    street: formattedAddress
  };
};

function Address({ getFieldProps, setFieldValue, formValues, hide = false,notDisplayTitle=false,displayedFor, disabled = false }) {
  const imageUploadInputRef = useRef();
  const imgUpload = useMutation(issueImgUpload);
  const [imageUploadPending, setImageUploadPending] = useState(false);

  const handlePlacesChanged = (places) => {
    const {
      address_components,
      geometry: { location },
      formatted_address,
    } = places[0];

    const parsed = parseGoogleAddress(address_components, formatted_address);
    if (parsed) {
      setFieldValue("country", parsed.country);
      setFieldValue("state", parsed.state);
      setFieldValue("zipCode", parsed.zipCode);
      setFieldValue("city", parsed.city);
      setFieldValue("street", parsed.street);
      setFieldValue("locationLatitude", location.lat());
      setFieldValue("locationLongitude", location.lng());
    }
  };
  const handleAmenitiesChange = (event) => {
    const {
      target: { value },
    } = event;
    setFieldValue("amenities",
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    ); 
  }

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    const base64 = [];
    setImageUploadPending(!0);
    for(let file of files) {
      base64.push(await fileToBase64(file));
    }

    base64.forEach((b) => {
      imgUpload.mutate(
        {
          image: b,
        },
        {
          onSuccess: (response) => {
            setImageUploadPending(false);
            // const updatedAttachmentObj = {
            //   ...values.attachment,
            //   url: response.data.data,
            // };
            const photos = formValues.photos.concat([response.data.data]);
            setFieldValue("photos", photos);
            // setUploadedImageUrl(response.data.data);
          },
        }
      );
    })
  };

  return (
    <Grid container spacing={3} className="mb-5">
      {!notDisplayTitle &&<Grid item xs={12} lg={12}>
        <Typography
          variant="subtitle1"
          gutterBottom
        >
          Address
        </Typography>
        <Divider />
      </Grid>}
      <Grid item xs={12} lg={6}>
        <Map
          onPlaces={disabled ? () => {} : handlePlacesChanged}
          lat={formValues.locationLatitude}
          lng={formValues.locationLongitude}
          address={formValues.street}
          disabled={disabled}
        />
      </Grid>
      {!hide && (
        <Grid item xs={12} lg={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={12}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Latitude"
                  size="small"
                  type="number"
                  inputProps={{ min: -90, max: 90, step: "any" }}
                  disabled={disabled}
                  {...getFieldProps("locationLatitude")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={12}>
              <FormControl fullWidth size="small">
                <TextField
                  label="Longitude"
                  size="small"
                  type="number"
                  inputProps={{ min: -180, max: 180, step: "any" }}
                  disabled={disabled}
                  {...getFieldProps("locationLongitude")}
                />
              </FormControl>
            </Grid>
           {displayedFor==="charger" &&<> <Grid item xs={12} lg={12}>
                <InputLabel id="demo-multiple-chip-label">Amenities</InputLabel>
              <FormControl fullWidth>
                <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={formValues.amenities && formValues.amenities.length?formValues.amenities.filter((amenity)=>!is.empty(amenity)):[]}
                  onChange={handleAmenitiesChange}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {AMENITIES.map((am) => (
                    <MenuItem
                      key={am.value}
                      value={am.value}
                      // style={getStyles(name, personName, theme)}
                    >
                      {am.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={12}>
              <Box style={{ border: "2px solid #C4C4C4", borderRadius: 4, padding: 24 }}>
                <Stack direction="column" alignItems="center" gap={3}>
                  <Typography color="rgba(0, 0, 0, 0.60)" textAlign="center" variant="body2">You're encouraged to add a photo. It will help other users to have a better idea of the place.</Typography>
                  <Icon icon="tabler:camera-plus" width={71} height={55} color="#d9d9d9" hFlip={true} />
                  <LoadingButton
                    variant="contained"
                    onClick={() => imageUploadInputRef.current.click()}
                    loading={imageUploadPending}
                    loadingIndicator="Loading…"
                    startIcon={<Icon icon="ic:baseline-plus"></Icon>}
                  >
                    Upload
                    <input
                      accept="image/jpeg,image/png,image/jpg"
                      type="file"
                      hidden
                      multiple
                      id="issue-image"
                      ref={imageUploadInputRef}
                      onChange={handleImageUpload}
                    />
                  </LoadingButton>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={12}>
              {  formValues.photos && 
              <ImageList cols={3}>
                { !is.empty(formValues.photos) && formValues.photos.filter(url=>!is.empty(url)).map((url) => (
                  <ImageListItem key={`${url}-key`}>
                    <img
                      src={url}
                      alt={"attachment"}
                      style={{
                        maxHeight: "170px",
                        maxWidth: "250px",
                      }}
                      loading="eager"
                    />
                </ImageListItem>
                ))}
              </ImageList>
          }
            </Grid>
            </>
            }
          </Grid>
        </Grid>
      )}
      {["charger"].includes(displayedFor) && <>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            label="Operator Number 1"
            size="small"
            {...getFieldProps("operator_number1")}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            label="Operator Number 2"
            size="small"
            {...getFieldProps("operator_number2")}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth size="small">
          <TextField
            label="Operator Email"
            size="small"
            {...getFieldProps("operator_email")}
          />
        </FormControl>
      </Grid></>}
    </Grid>
  );
}

export default memo(Address);
